import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import SearchBar from "../components/SearchBar";
import Stars from "../components/Stars";
import { listarEmpresas } from "../services/api";

const SELO = {
  ra:   { txt: "Selo eaiRH", cls: "bg-green-100 text-green-800" },
  good: { txt: "Boa reputação", cls: "bg-blue-100 text-blue-800" },
  mid:  { txt: "Regular", cls: "bg-yellow-100 text-yellow-800" },
  bad:  { txt: "Não recomendada", cls: "bg-red-100 text-red-800" },
  none: { txt: "Sem reputação", cls: "bg-gray-100 text-gray-600" },
};
function corNota(s) { return s >= 7 ? "text-green-600" : s >= 5 ? "text-yellow-600" : "text-red-600"; }

// Ordenações disponíveis. "responde" destaca quem MENOS responde (entre as que têm relatos) — reforça o propósito.
// Desempate por RECÊNCIA: entre notas iguais, quem tem relato mais novo vem primeiro — informação fresca
// vale mais para quem vai se candidatar, e o ranking deixa de ficar congelado na ordem do banco.
const recente = (e) => (e.ultimoRelatoEm ? Date.parse(e.ultimoRelatoEm) : 0);
const ORDENS = {
  reputacao: { label: "Maior reputação", fn: (a, b) => b.score - a.score || recente(b) - recente(a) || b.totalDenuncias - a.totalDenuncias },
  relatos: { label: "Mais relatos", fn: (a, b) => (b.totalDenuncias + (b.totalElogios || 0)) - (a.totalDenuncias + (a.totalElogios || 0)) || recente(b) - recente(a) },
  responde: {
    label: "Quem menos responde",
    fn: (a, b) => {
      // empresas sem relatos vão pro fim (não há o que responder); entre as com relatos, menor % de resposta primeiro
      if ((a.totalDenuncias > 0) !== (b.totalDenuncias > 0)) return a.totalDenuncias > 0 ? -1 : 1;
      return a.indiceResposta - b.indiceResposta || b.totalDenuncias - a.totalDenuncias || recente(b) - recente(a);
    },
  },
};

export default function CompaniesPage() {
  const [empresas, setEmpresas] = useState([]);
  const [busca, setBusca] = useState("");
  const [ordem, setOrdem] = useState("reputacao");
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const navigate = useNavigate();
  const reqSeq = useRef(0); // guarda contra resposta fora de ordem (a carga "todas" é pesada e pode chegar após uma busca leve)

  const carregar = useCallback(async (b) => {
    const seq = ++reqSeq.current;
    setCarregando(true); setErro(false);
    try { const data = await listarEmpresas(b); if (seq === reqSeq.current) setEmpresas(data); }
    catch { if (seq === reqSeq.current) { setErro(true); setEmpresas([]); } }
    finally { if (seq === reqSeq.current) setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <h1 className="text-2xl font-bold text-text mb-1">Empresas avaliadas</h1>
        <p className="text-gray-600 mb-5">Veja a reputação das empresas em seus processos seletivos.</p>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="max-w-md flex-1 min-w-[200px]">
            <SearchBar placeholder="Buscar empresa..." onSearch={(b) => { setBusca(b); carregar(b); }} />
          </div>
          <label className="text-sm text-gray-600 flex items-center gap-2">
            Ordenar por:
            <select value={ordem} onChange={(e) => setOrdem(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-2 text-sm focus:ring-2 focus:ring-primary outline-none">
              {Object.entries(ORDENS).map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {carregando && <p className="text-gray-500 col-span-2">Carregando empresas...</p>}
          {!carregando && erro && <p className="text-red-600 col-span-2">Não foi possível carregar as empresas. Tente recarregar a página.</p>}
          {!carregando && !erro && [...empresas].sort(ORDENS[ordem].fn).map((e) => {
            const selo = SELO[e.selo] || SELO.none;
            return (
              <article key={e.id} onClick={() => navigate(`/empresas/${e.slug || e.id}`)}
                role="button" tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); navigate(`/empresas/${e.slug || e.id}`); } }}
                aria-label={`Ver reputação de ${e.nome}`}
                className="cursor-pointer rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary transition flex items-center gap-4">
                <div className={`text-3xl font-extrabold w-14 text-center ${e.selo === "none" ? "text-gray-300" : corNota(e.score)}`} title={e.selo === "none" ? "Reputação em formação" : `${e.score.toFixed(1)}/10`}>{e.selo === "none" ? "–" : e.score.toFixed(1)}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900">
                    {e.nome}{e.verificada && <span className="ml-1 text-xs text-blue-600">✔</span>}
                  </h3>
                  <p className="text-xs text-gray-500">{[e.setor, e.cidade].filter(Boolean).join(" · ")}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${selo.cls}`}>{e.classificacao}</span>
                    {/* Elogio conta como relato no placar da lista — só não entra em "reclamações". */}
                    <span className="text-xs text-gray-500">{e.totalDenuncias + (e.totalElogios || 0)} relato(s)</span>
                    {e.totalElogios > 0 && <span className="text-[11px] font-semibold text-emerald-700">👏 {e.totalElogios}</span>}
                  </div>
                  {(e.totalDenuncias + (e.totalElogios || 0)) > 0 && e.selo !== "none" && <div className="mt-1"><Stars score={e.score} size={15} /></div>}
                </div>
              </article>
            );
          })}
          {!carregando && !erro && empresas.length === 0 && (
            <p className="text-gray-500 col-span-2">Nenhuma empresa encontrada{busca ? ` para "${busca}"` : ""}.</p>
          )}
        </div>
      </main>
    </div>
  );
}
